import { useEffect, useState } from 'react';
import { useLocation, Navigate } from 'react-router';
import { useSlugs } from '../hooks/useSlugs';

// Import all page components
import { ROIPage } from '../pages/ROIPage';
import AboutPageNew from '../pages/AboutPageNew';
import { ProcessPage } from '../pages/ProcessPage';
import { ContactPage } from '../pages/ContactPage';
import { VideoExamplesPage } from '../pages/VideoExamplesPage';
import { HomePage } from '../pages/HomePage';
import { BundlesPage } from '../pages/BundlesPage';

// Map of page keys to their components
const PAGE_COMPONENTS: Record<string, React.ComponentType> = {
  'homepage': HomePage,
  'about': AboutPageNew,
  'process': ProcessPage,
  'contact': ContactPage,
  'roi': ROIPage,
  'videos': VideoExamplesPage,
  'bundles': BundlesPage,
};

export function DynamicPageRouter() {
  const location = useLocation();
  const { slugs, loading: slugsLoading } = useSlugs();
  const [pageComponent, setPageComponent] = useState<React.ComponentType | null>(null);
  const [redirectTo, setRedirectTo] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);

  // Resolve slug whenever location or slugs change
  useEffect(() => {
    if (slugsLoading || !slugs) {
      return;
    }

    const currentPath = location.pathname;

    // 1️⃣ Check if current path matches ANY page slug
    let foundPage: keyof typeof PAGE_COMPONENTS | null = null;
    
    for (const [pageKey, slug] of Object.entries(slugs.pages)) {
      if (slug === currentPath) {
        foundPage = pageKey as keyof typeof PAGE_COMPONENTS;

        break;
      }
    }

    // 2️⃣ If found, render the page component
    if (foundPage && PAGE_COMPONENTS[foundPage]) {
      setPageComponent(() => PAGE_COMPONENTS[foundPage]);
      setRedirectTo(null);
      setNotFound(false);
      return;
    }

    // 3️⃣ If not found, check if this is an OLD slug that needs redirect
    // Common old slugs (hardcoded mapping for backward compatibility)
    const OLD_SLUG_REDIRECTS: Record<string, string> = {
      '/roi': 'roi',
      '/about': 'about',
      '/process': 'process',
      '/contact': 'contact',
      '/bundles': 'bundles',
      '/videos': 'videos',
    };

    // Check if current path is a known old slug
    const pageKeyForOldSlug = OLD_SLUG_REDIRECTS[currentPath];
    if (pageKeyForOldSlug && slugs.pages[pageKeyForOldSlug]) {
      const newSlug = slugs.pages[pageKeyForOldSlug];
      
      // Only redirect if new slug is different from current path
      if (newSlug !== currentPath) {

        setRedirectTo(newSlug);
        return;
      }
    }

    // 4️⃣ Check for dynamic old slugs from backend (future enhancement)
    // TODO: Implement backend slug history lookup
    // const oldSlugMapping = await fetchOldSlugMapping(currentPath);
    // if (oldSlugMapping) {
    //   setRedirectTo(oldSlugMapping.newSlug);
    //   return;
    // }

    // 5️⃣ Not found anywhere

    setNotFound(true);
  }, [location.pathname, slugs, slugsLoading]);

  // Loading state
  if (slugsLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#2F2E5C] to-[#1F1E3C]">
        <div className="text-center">
          <div className="text-6xl mb-4 animate-pulse">⏳</div>
          <p className="text-white text-lg">Loading page...</p>
        </div>
      </div>
    );
  }

  // 301 Redirect (replace=true for proper redirect)
  if (redirectTo) {

    return <Navigate to={redirectTo} replace />;
  }

  // 404 Not Found
  if (notFound) {

    return <Navigate to="/" replace />;
  }

  // Render matched page
  const PageComponent = pageComponent;
  return PageComponent ? <PageComponent /> : null;
}